"use client"

import { useCallback } from "react"
import { toast } from "sonner"
import { useDeviceStore } from "@/lib/device-store"

/**
 * Calibration is diagnostic data. Playback uses the user's explicit sync adjustment.
 */
export function describeSyncDelay(ms: number): string {
  if (!ms) return "No measured offset"
  return `Measured offset: ${Math.abs(ms)} ms (not applied automatically)`
}

/**
 * One implementation of "Auto Calibrate" for every menu that offers it (Devices page, VR player 2D and
 * immersive, booru modal and mini player, Goonpals, Gooncave). It measures every connected Handy,
 * records each one's measured offset, and tells the user what happened. Nothing is ever invented: a device
 * that can't be measured is left as it was.
 */
export function useDeviceCalibration() {
  const devices = useDeviceStore((s) => s.devices)
  const activeDeviceId = useDeviceStore((s) => s.activeDeviceId)
  const isCalibrating = useDeviceStore((s) => s.isCalibrating)
  const autoCalibrateAll = useDeviceStore((s) => s.autoCalibrateAll)

  const connectedHandys = devices.filter((d) => d.type === "handy" && d.isConnected && !!d.connectionKey)
  const activeDevice = devices.find((d) => d.id === activeDeviceId) ?? null

  const calibrate = useCallback(async () => {
    if (useDeviceStore.getState().isCalibrating) return
    const connected = useDeviceStore
      .getState()
      .devices.filter((d) => d.type === "handy" && d.isConnected && d.connectionKey)
    if (connected.length === 0) {
      toast.error("Connect a Handy first", {
        description: "Auto calibration measures a connected Handy. Other devices can't be measured yet; set their sync delay by hand.",
      })
      return
    }
    const results = await autoCalibrateAll()
    const measured = results.filter((r) => r.latencyMs !== undefined)
    const failed = results.filter((r) => r.latencyMs === undefined)
    if (measured.length > 0) {
      toast.success(
        measured.length === 1
          ? `Measured latency: ${measured[0].latencyMs} ms`
          : `Measured ${measured.length} devices`,
        { description: measured.map((r) => `${r.name}: ${r.latencyMs} ms`).join("  ·  ") },
      )
    }
    if (failed.length > 0) {
      toast.error(`Couldn't measure ${failed.map((r) => r.name).join(", ")}`, {
        description: "Their sync delay was left unchanged.",
      })
    }
  }, [autoCalibrateAll])

  return {
    calibrate,
    isCalibrating,
    /** There is at least one connected Handy that can be measured. */
    canCalibrate: connectedHandys.length > 0,
    connectedCount: connectedHandys.length,
    activeDevice,
    /** The active device's own sync delay in ms (negative = fires earlier). */
    activeSyncDelay: activeDevice?.syncDelayMs ?? 0,
    /** The last measured one-way latency of the active device, if any. */
    activeLatency: activeDevice?.latencyMs ?? 0,
  }
}
